/**
 * MODERAÇÃO GERAL DAS INSTITUIÇÕES — o admin geral, regras de 06/10 (repo instituicoes-ensino).
 *
 * Três níveis de gestão, e este é o da plataforma: o admin geral aprova o 1º educador de cada
 * instituição (isso a ATIVA e o faz moderador — caso A), recusa o pedido, e suspende ou reativa.
 * Antes desta tela, ativar uma escola no HQ-Lab era feito à mão no console do banco (T2.74).
 *
 * A rota é aberta, como /admin/regras: a trava de verdade está no banco — só quem existe em
 * `administradores/{uid}` (coleção que cliente nenhum escreve) muda a situação de uma instituição.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { auth } from "../../api/firebase.class";
import { souAdministrador } from "../../admin/o-acesso-ao-painel";
import {
  ativarPeloEducador,
  instituicoesNaSituacao,
  mudarSituacaoDaInstituicao,
  recusarAtivacao,
  vinculosDaInstituicao,
} from "../../instituicao/api";
import { Instituicao, Vinculo } from "../../instituicao/modelo";
import Nav from "../../components/nav";

import "./regras.scss";
import "../../instituicao/minha-instituicao.scss";

type Aba = "aguardando" | "ativas" | "suspensas";

export default function ModeracaoDeInstituicoes() {
  const [podeMexer, setPodeMexer] = useState<boolean | null>(null);
  const [aba, setAba] = useState<Aba>("aguardando");
  const [aguardando, setAguardando] = useState<{ inst: Instituicao; pedidos: Vinculo[] }[]>([]);
  const [ativas, setAtivas] = useState<Instituicao[]>([]);
  const [suspensas, setSuspensas] = useState<Instituicao[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  const carregar = useCallback(async () => {
    const [sol, at, sus] = await Promise.all([
      instituicoesNaSituacao("solicitada"),
      instituicoesNaSituacao("ativa"),
      instituicoesNaSituacao("suspensa"),
    ]);
    const comPedidos = await Promise.all(
      sol.map(async (inst) => ({
        inst,
        pedidos: (await vinculosDaInstituicao(inst.id, "pendente")).filter((v) => v.papel === "educador"),
      }))
    );
    setAguardando(comPedidos);
    setAtivas(at);
    setSuspensas(sus);
  }, []);

  useEffect(() => {
    let vivo = true;
    void souAdministrador().then((v) => {
      if (!vivo) return;
      setPodeMexer(v);
      if (v) void carregar();
    });
    document.title = "Instituições · HQ-Lab";
    return () => {
      vivo = false;
      document.title = "HQ-Lab";
    };
  }, [carregar]);

  async function fazer(fn: () => Promise<{ ok: boolean; erro?: string }>, sucesso: string) {
    if (ocupado) return;
    setOcupado(true);
    setAviso(null);
    const r = await fn();
    setOcupado(false);
    setAviso(r.ok ? { tipo: "ok", texto: sucesso } : { tipo: "erro", texto: r.erro || "Não deu. Tente de novo." });
    if (r.ok) await carregar();
  }

  const adminUid = auth?.currentUser?.uid ?? "";

  if (podeMexer === null) {
    return (
      <>
        <Nav />
        <main className="regras-painel">
          <h1>Instituições do HQ-Lab</h1>
          <p>Conferindo o seu acesso…</p>
        </main>
      </>
    );
  }

  if (!podeMexer) {
    return (
      <>
        <Nav />
        <main className="regras-painel">
          <h1>Instituições do HQ-Lab</h1>
          <p className="regras-sem-acesso">
            Esta página é do administrador geral da plataforma. A sua conta não está na lista.
          </p>
          <p>
            <Link to="/">Voltar ao início</Link>
          </p>
        </main>
      </>
    );
  }

  return (
    <>
      <Nav />
      <main className="regras-painel" data-testid="moderacao-geral">
        <h1>Instituições do HQ-Lab</h1>
        <p className="regras-explicacao">
          Aprovar o primeiro educador de uma escola ativa a escola, e ele passa a aceitar quem pede
          para entrar. Depois disso, quem modera a escola são os educadores dela.
        </p>

        <div className="inst-acoes" role="tablist">
          {(
            [
              ["aguardando", `Aguardando (${aguardando.length})`],
              ["ativas", `Ativas (${ativas.length})`],
              ["suspensas", `Suspensas (${suspensas.length})`],
            ] as [Aba, string][]
          ).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={aba === id}
              className={aba === id ? "botao-secundario" : "botao-sair"}
              onClick={() => setAba(id)}
            >
              {rotulo}
            </button>
          ))}
        </div>

        {aviso && (
          <p className={`inst-aviso inst-aviso-${aviso.tipo}`} role="status">
            {aviso.texto}
          </p>
        )}

        {aba === "aguardando" &&
          (aguardando.length === 0 ? (
            <p className="ajuda">Nenhuma escola esperando ativação.</p>
          ) : (
            <ul className="inst-lista" data-testid="instituicao-aguardando">
              {aguardando.map(({ inst, pedidos }) => (
                <li key={inst.id} className="inst-item inst-pendente">
                  <div>
                    <strong>{inst.nome}</strong>
                    <span className="inst-detalhe">
                      {inst.cidade} · código {inst.id.split(":")[1] ?? inst.id}
                    </span>
                    {pedidos.length === 0 ? (
                      <span className="inst-detalhe">Nenhum educador pedindo — o pedido foi desfeito.</span>
                    ) : (
                      pedidos.map((p) => (
                        <span key={p.id} className="inst-acoes">
                          <span className="inst-detalhe">
                            {p.nome || p.email || p.uid} pede para ser o educador
                          </span>
                          <button
                            type="button"
                            className="botao-secundario"
                            disabled={ocupado}
                            onClick={() =>
                              fazer(
                                () => ativarPeloEducador(inst.id, p, adminUid),
                                `${inst.nome} ativada, com ${p.nome || p.email || "o educador"} como moderador.`
                              )
                            }
                          >
                            Aprovar e ativar
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <span className="inst-acoes">
                    <button
                      type="button"
                      className="botao-sair"
                      disabled={ocupado}
                      onClick={() => fazer(() => recusarAtivacao(inst.id, pedidos, adminUid), `${inst.nome}: pedido recusado.`)}
                    >
                      Recusar
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          ))}

        {aba === "ativas" &&
          (ativas.length === 0 ? (
            <p className="ajuda">Nenhuma escola ativa.</p>
          ) : (
            <ul className="inst-lista">
              {ativas.map((inst) => (
                <li key={inst.id} className="inst-item">
                  <div>
                    <strong>{inst.nome}</strong>
                    <span className="inst-detalhe">{inst.cidade}</span>
                  </div>
                  <span className="inst-acoes">
                    <button
                      type="button"
                      className="botao-sair"
                      disabled={ocupado}
                      onClick={() => fazer(() => mudarSituacaoDaInstituicao(inst.id, "suspensa", adminUid), `${inst.nome} suspensa.`)}
                    >
                      Suspender
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          ))}

        {aba === "suspensas" &&
          (suspensas.length === 0 ? (
            <p className="ajuda">Nenhuma escola suspensa.</p>
          ) : (
            <ul className="inst-lista">
              {suspensas.map((inst) => (
                <li key={inst.id} className="inst-item">
                  <div>
                    <strong>{inst.nome}</strong>
                    <span className="inst-detalhe">{inst.cidade}</span>
                  </div>
                  <span className="inst-acoes">
                    <button
                      type="button"
                      className="botao-secundario"
                      disabled={ocupado}
                      onClick={() => fazer(() => mudarSituacaoDaInstituicao(inst.id, "ativa", adminUid), `${inst.nome} reativada.`)}
                    >
                      Reativar
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          ))}
      </main>
    </>
  );
}
